/** Every mode, for a caller that needs to enumerate or validate rather than name one. */
export const GRADING_MODE_IDS = ['graded', 'survey', 'optional'];
/**
 * Narrow an unknown to a mode, defaulting to `graded`.
 *
 * Every block's `initData` did this by hand with the same
 * `['graded','survey','optional'].includes(...)` line and the same cast. The cast is the reason to
 * share it: written out per block, each one is an unchecked assertion that the runtime check and
 * the type agree, and they only agree because the same three strings were typed correctly twelve
 * times.
 */
export function asGradingMode(raw) {
    return GRADING_MODE_IDS.includes(raw) ? raw : 'graded';
}
/**
 * Which default state carries the POSITIVE outcome under this mode.
 *
 * A graded block has a Correct state; a survey has Any Answer; an optional block has Any Response.
 * Exactly one of the three is live at a time, and every block has to know which — to title the
 * card, to chip it in the test bar, to decide what the evaluator fires.
 *
 * That three-way choice was written as an if-ladder twice per block across eleven blocks. Nothing
 * had gone wrong, because it is short enough to get right; what it cost is that adding a fourth
 * mode would mean finding twenty-two ladders, and `evaluate*` would have no name for the concept it
 * branches on. Lives here for the same reason `GradingMode` does: the student interface will need
 * it, and grading is decided in this package.
 */
export function positiveTriggerFor(mode) {
    return mode === 'survey' ? 'any_answer' : mode === 'optional' ? 'any_response' : 'correct';
}
/** The three names, as a set — the inverse question, from one list. */
const POSITIVE_TRIGGERS = new Set(['correct', 'any_answer', 'any_response']);
/**
 * WHETHER A FIRED STATE MEANS "THIS BLOCK IS SATISFIED" — the pass state of whichever mode the
 * block is in, asked without knowing the mode.
 *
 * `resolveFlow` is handed `FeedbackState[]` and nothing else: no block data, no grading mode. It
 * does not need them. A block that fired `correct`, `any_answer` or `any_response` passed, and
 * one that fired anything else did not — the evaluator has already applied the mode by choosing
 * which state to return. That is what lets All Correct be a POSITIVE test ("every block passed")
 * rather than a negative one ("nothing said wrong"), with no extra parameter to thread.
 *
 * Green therefore means "this block is satisfied for the purposes of All Correct", which
 * coincides with "right" in graded and means "answered" in survey and optional.
 */
export function isPositiveTrigger(trigger) {
    return POSITIVE_TRIGGERS.has(trigger);
}
/**
 * What a PARTICIPATION mode (survey, optional) returns: specific before general.
 *
 * ## The ordering is not inverted, and this is where that is settled
 *
 * `correct` / `any_answer` / `any_response` were described as "three names for the one
 * positive-outcome state", which made the ordering look inconsistent — correct is checked FIRST
 * in graded, yet `any_answer` would be checked LAST here. That reading was only true while
 * nothing else could fire.
 *
 * Once targeted states exist, `any_answer` is the analogue of graded's `incorrect` default: the
 * CATCH-ALL, not the positive outcome. So the rule is the same in every mode —
 *
 *   graded    correct (terminal) -> targeted -> incorrect
 *   survey                          targeted -> any_answer
 *   optional                        targeted -> any_response
 *
 * — specific before general, with graded merely adding a correctness short-circuit on top,
 * because it is the only mode that has a notion of being right.
 *
 * ## Why the targeted states come back NEUTRAL
 *
 * A targeted state's evaluation mirrors its option's role, and roles are meaningless outside
 * graded ("survey/optional never consult an option's role"). A participation answer is neither
 * right nor wrong, so it must not arrive wearing a ✓ or a ✗ — which is also exactly what a
 * narrative choice should look like on screen.
 *
 * It is also why Flow's routing keys on "a targeted state fired" rather than on an evaluation:
 * there is no longer an evaluation that could distinguish one choice from another.
 *
 * ## Backwards compatible by construction
 *
 * `matched` is empty for every block that authors no targeted states, which is all of them today,
 * so the catch-all fires exactly as it always has.
 *
 * @param matched what the BLOCK'S OWN targeted matcher produced. Never re-derived here: matching
 *   depends on how each block reads an answer (a selection, a normalised word, a value against a
 *   range), and a second implementation of that is precisely what this file exists to prevent.
 */
export function participationOutcome(states, mode, matched) {
    if (matched.length > 0)
        return matched.map(s => ({ ...s, evaluation: 'neutral' }));
    const trigger = mode === 'optional' ? 'any_response' : 'any_answer';
    const fallback = states.find(s => s.type === 'default' && s.trigger === trigger);
    return fallback ? [fallback] : [];
}
/**
 * What an OPTIONAL block fires: its catch-all, whatever the student did.
 *
 * Named rather than inlined eleven times because it is one decision, not eleven — see
 * `targetedFiresIn` for why optional is inert. Every evaluator's optional branch is this line.
 *
 * It replaces `NO_RESPONSE_TRIGGER` and `blankTargeted`, which existed to let optional speak to a
 * skip. A skip and an answer get the same card again: telling them apart is the attempt record's
 * job, not a feedback state's, and the trigger cost more than the distinction was worth.
 */
export function optionalOutcome(states) {
    return participationOutcome(states, 'optional', []);
}
/**
 * The evaluation a feedback state DISPLAYS as, which is not always the one it carries.
 *
 * A targeted state's stored evaluation mirrors its option's role, and roles are not consulted
 * outside graded — so in a participation mode the stored value is a leftover from a mode the
 * block is not in. Showing it would put a ✗ on a card that fires neutrally, and on a narrative
 * choice there is nothing to be wrong about in the first place.
 *
 * Stated here so the editor and `participationOutcome` cannot disagree about it: the evaluator
 * neutralises what it returns, and this neutralises what the panel draws, from one rule.
 */
export function displayEvaluation(mode, evaluation) {
    return mode === 'graded' ? evaluation : 'neutral';
}
/**
 * The roles an answer list CARRIES, reduced to the ones the current mode actually consults.
 *
 * Survey and optional have no notion of being right, so outside graded every role is `neutral` —
 * not as a cosmetic touch-up but because `role === 'correct'` is the predicate roughly thirty
 * call sites across six blocks use to decide whether a targeted state may exist, whether the
 * trigger picker offers an answer, what colour a chip is, and whether to raise "this feedback
 * targets a value now marked correct". Each of those had to remember the mode on its own, and
 * most did not: the correct keyword stayed out of its own trigger picker, Scale warned about a
 * correct value in a mode that has none, and FITB could never author a target at all.
 *
 * Applying it ONCE, where the effective list is derived, makes every one of those sites right
 * without knowing the mode exists. `invariants.test.ts` pins that no block reads `.role` for
 * correctness outside a derivation that has been through here.
 *
 * ## This is display and authoring state, never storage
 *
 * The stored roles are untouched, so flipping to survey and back restores the graded setup
 * exactly. Callers must keep writing role changes to their RAW option list — a mutator that
 * round-trips through this would persist the neutralised copy and silently erase the teacher's
 * graded authoring the moment they switched modes.
 *
 * Precedent for the shape: `resolveEffectiveOptions` already collapses every role to neutral when
 * a branch set fails to cover its domain, for the same "nothing here is correct any more" reason.
 *
 * The scalar {@link roleForMode} is the same rule for one role at a time — the shape
 * `displayEvaluation` already uses, for a call site holding a single option rather than a list.
 */
export function roleForMode(mode, role) {
    return mode === 'graded' ? role : 'neutral';
}
/** The list form of {@link roleForMode}, for a derivation that hands a whole answer list on. */
export function rolesForMode(mode, items) {
    if (mode === 'graded')
        return items;
    return items.map(i => (i.role === 'neutral' ? i : { ...i, role: 'neutral' }));
}
/**
 * "The answer the teacher authored" — a targeted trigger for the ACCEPTED BAND itself.
 *
 * In graded the band produces `correct`, which is terminal. Outside graded there is nothing to be
 * right about, so the same band becomes just another answer the teacher can speak to and route
 * on. That is the whole definition: **Set Answer is the correct state minus the verdict.**
 *
 * It exists because the value blocks express their answer as a BAND rather than as a listed
 * entry, so unlike MCQ's correct option there was nothing to point a targeted state at. Authors
 * had to retype the answer as `exact:42`, which then went stale the moment they edited it.
 *
 * Resolved at evaluation time against the block's own band derivation, never stored as a literal
 * — that is what keeps it in step with an edited answer, and what lets it work with a formula
 * answer whose value is not known until the roll.
 *
 * NOT terminal. A teacher writing "you're probably right, but let's explore the logic" needs it
 * to fire AND let routing happen, which is the opposite of what `correct` does.
 */
export const SET_ANSWER_TRIGGER = 'set_answer';
/** The band target, if the teacher authored one. */
export function setAnswerTargeted(states) {
    return states.filter(s => s.type === 'targeted' && s.trigger === SET_ANSWER_TRIGGER);
}
/**
 * Whether a targeted trigger can fire in this mode at all.
 *
 * Two rules, and both describe a MODE rather than a trigger.
 *
 * OPTIONAL is INERT: one catch-all and nothing else — no targeted states, no gates, no skip state
 * of its own. Splitting the answer space there bought almost nothing a Survey block does not do
 * better, and it cost a great deal: a trigger that had to be hidden from every other mode, a state
 * the Flow rungs could not categorise, and an optional block able to pin a student on a question
 * they were told they could skip. A block nobody is grading records what happened and gets out of
 * the way.
 *
 * `Answer` is unreachable in GRADED, where Correct is checked first and is terminal. It already
 * behaved that way in the evaluators; what was missing was the editor agreeing, so the card sat in
 * the panel looking live and counted towards the coverage that decides whether the catch-all is
 * reachable.
 *
 * Hidden, never deleted — the same promise every other stale targeted state makes. Switch back and
 * the card and its text are still there.
 */
export function targetedFiresIn(trigger, mode) {
    if (mode === 'optional')
        return false;
    if (trigger === SET_ANSWER_TRIGGER)
        return mode !== 'graded';
    return true;
}
/** Every targeted state that can fire in this mode. The list form of `targetedFiresIn`. */
export function visibleTargeted(states, mode) {
    return states.filter(s => s.type === 'targeted' && targetedFiresIn(s.trigger, mode));
}
//# sourceMappingURL=grading-mode.js.map